import Const from 'const';
import {StackItemStructure} from './interfaces/stack-item-interface';

export function isPlayerStackType(stackWith: string): boolean {
    return stackWith === Const.STACK_WITH_PLAYER;
}

function assertExclusionSettingsAreValid(settings: StackItemStructure, players) {
    const {
        keyPlayers,
        stackWith,
        selectedPlayers,
        exclusionSettings: {isExclusionEnabled, exclusionType, excludedPlayers, excludedTeams},
    } = settings;

    if (!isExclusionEnabled) {
        return;
    }

    const isPlayerStackType = stackWith === Const.STACK_WITH_PLAYER;
    const isPlayerExclusionType = exclusionType === Const.STACK_WITH_PLAYER_EXCLUSION;

    if (isPlayerExclusionType) {
        if (isEmpty(excludedPlayers)) {
            throw new Error('You should select an excludable player.');
        }
        if (!isEmpty(arrayDuplicates(keyPlayers, excludedPlayers))) {
            throw new Error('Key player cannot be excluded.');
        }
        if (isPlayerStackType && arrayDuplicates(selectedPlayers, excludedPlayers)) {
            throw new Error('Stackable players cannot be excluded.');
        }
    } else {
        if (isEmpty(excludedTeams)) {
            throw new Error('You should select an excludable team.');
        }
        for (const keyPlayer of keyPlayers) {
            const player = players.getPlayer(keyPlayer);
            if (inArray(player.team, excludedTeams)) {
                throw new Error('Key player cannot be excluded by team.');
            }
        }
        if (isPlayerStackType) {
            for (let selectedPlayer of selectedPlayers) {
                const player = players.getPlayer(selectedPlayer);
                if (inArray(player.team, excludedTeams)) {
                    throw new Error('Player cannot be excluded by team.');
                }
            }
        }
    }
}

export function assertSettingsAreValid(settings: StackItemStructure, players) {
    const {keyPlayers, keyPositions, stackWith, selectedPlayers, selectedPositions, numberPlayers} = settings;

    if (isEmpty(keyPositions)) {
        throw new Error('You should select the key position.');
    }

    if (stackWith === Const.STACK_WITH_POSITION) {
        if (isEmpty(selectedPositions)) {
            throw new Error('You should select a position.');
        }
    } else {
        if (isEmpty(selectedPlayers) || isEmpty(keyPlayers)) {
            throw new Error('You should select a player.');
        }
        if (!isEmpty(arrayDuplicates(keyPlayers, selectedPlayers))) {
            throw new Error('You should select different players.');
        }
        if (selectedPlayers.length < numberPlayers) {
            throw new Error('You should add more players.');
        }
    }

    assertExclusionSettingsAreValid(settings, players);
}

export class StackItemFactory {
    static getStackItemStructure(changeableProperties = {}): StackItemStructure {
        return {
            keyPositions: [],
            keyPlayers: [],
            keyPlayerLineupPosition: Const.FLEX_MODE,
            stackWith: Const.STACK_WITH_POSITION,
            type: Const.STACKS_TYPE_AT_LEAST,
            numberPlayers: 0,
            selectedPositions: [],
            selectedPlayers: [],
            isSameTeam: true,
            isPrimaryStack: true,
            isStackItemForcible: false,
            stackBoostPercent: 0,
            exclusionSettings: {
                isExclusionEnabled: false,
                exclusionType: Const.STACK_WITH_TEAM_EXCLUSION,
                excludedTeams: [],
                excludedPlayers: [],
            },
            ...changeableProperties,
        };
    }
}

export const secondaryStackCombinationsMap = {
    [Const.SECONDARY_STACK_COMBINATION_TYPE_1]: StackItemFactory.getStackItemStructure({
        keyPositions: ['RB'],
        isSameTeam: false,
        selectedPositions: ['WR', 'TE'],
        numberPlayers: 1,
        isPrimaryStack: false,
    }),
    [Const.SECONDARY_STACK_COMBINATION_TYPE_2]: StackItemFactory.getStackItemStructure({
        keyPositions: ['WR'],
        isSameTeam: false,
        selectedPositions: ['WR', 'TE'],
        numberPlayers: 1,
        isPrimaryStack: false,
    }),
    [Const.SECONDARY_STACK_COMBINATION_TYPE_3]: StackItemFactory.getStackItemStructure({
        keyPositions: ['RB'],
        isSameTeam: true,
        selectedPositions: ['WR'],
        numberPlayers: 1,
        isPrimaryStack: false,
    }),
    [Const.SECONDARY_STACK_COMBINATION_TYPE_4]: StackItemFactory.getStackItemStructure({
        keyPositions: ['RB'],
        isSameTeam: true,
        selectedPositions: ['D', 'DEF', 'DST'],
        numberPlayers: 1,
        isPrimaryStack: false,
    }),
    [Const.SECONDARY_STACK_COMBINATION_TYPE_5]: StackItemFactory.getStackItemStructure({
        keyPositions: ['WR', 'TE'],
        isSameTeam: true,
        selectedPositions: ['WR', 'TE'],
        numberPlayers: 1,
        isPrimaryStack: false,
    }),
};
